/**
 * motionCardRun.ts
 * モーションを1つ再生するカード（Gesture / Dance）の共通実行処理。
 * バーチャルロボットの再生と実機への RPC を並列で待ち、両方が終わってから次のカードへ進む。
 */
import type { BatchCommandSummary, FlowCommandOptions, RobotCommandResult } from '@/connection/BatchRobotService';
import type { ExecContext, Logger } from '../../types';
import { nextFlowRequestId } from '../../atoms/flowRequestId';
import type { TimelineData } from '../../animation/animationUtils';
import type { CustomMotionConfig } from '../../config/customMotions';

/** モーション送信前の待機時間 */
const PRE_MOTION_WAIT_MS = 500;

type MotionCardRunParams = {
  /** ログの接頭辞。'Gesture' なら [Gesture] と出る */
  logTag: string;
  nodeKind: 'gesture' | 'customGesture' | 'dance' | 'walking';
  /** 実行中のノードID（RPC のログ紐付けに使う） */
  nodeId: string;
  /** 設定ファイル上のモーションID（文字列） */
  motionId: string;
  /** ログに出すモーション名。未設定なら '（未設定）' */
  motionLabel: string;
  /** VirtualRobot が完了通知を返さない場合の保険タイムアウト */
  virtualTimeoutMs: number;
  /** 自作ジェスチャー用のVirtualRobot再生データ */
  timeline?: TimelineData;
  /** VirtualRobotの再生中に物理エンジンを適用する */
  physicsEnabled?: boolean;
  /** 組み込みモーション番号を持たない場合は実機RPCを送らない */
  skipRobotMotion?: boolean;
  /** startMotion の代わりに実機へ送る命令 */
  robotCommand?: (
    robotService: NonNullable<ExecContext['robotService']>,
    options: FlowCommandOptions,
  ) => Promise<BatchCommandSummary>;
  /** 実機へ送る自作ジェスチャーのポーズ列 */
  customGesture?: CustomMotionConfig;
  requireVirtualCompletion?: boolean;
};

export const getVirtualMotionTimeline = (
  timeline: TimelineData | undefined,
  realRobotExecution: boolean,
): TimelineData | undefined => (
  timeline && realRobotExecution
    ? { ...timeline, soundKeyframes: [] }
    : timeline
);

const logBatchResult = (logTag: string, log: Logger, results: RobotCommandResult[]) => {
  results.forEach((result) => {
    if (result.status === 'fulfilled') {
      log(`[${logTag}][${result.robotId}] 成功`);
      return;
    }
    log(`[${logTag}][${result.robotId}] ${result.status}: ${result.message ?? result.errorCode ?? '不明なエラー'}`);
  });
};

export const runMotionCard = async (
  { logTag, nodeKind, nodeId, motionId, motionLabel, virtualTimeoutMs, timeline, physicsEnabled = false, skipRobotMotion = false, robotCommand, customGesture, requireVirtualCompletion = false }: MotionCardRunParams,
  ctx: ExecContext,
  log: Logger,
): Promise<void> => {
  if (requireVirtualCompletion && !ctx.setFlowMotion) {
    throw new Error('歩数指定の前進にはVirtualRobotが必要です。');
  }
  const parsedMotionNo = Number.parseInt(motionId, 10);
  const motionNo = Number.isNaN(parsedMotionNo) ? 0 : parsedMotionNo;
  if (!motionId || (!customGesture && Number.isNaN(motionNo))) {
    log(`[${logTag}] エラー: モーションがせんたくされていません`);
    return;
  }
  // モーション実行前に0.5秒待機
  await new Promise<void>((resolve) => {
    if (ctx.signal?.aborted) { resolve(); return; }
    const timeout = setTimeout(resolve, PRE_MOTION_WAIT_MS);
    ctx.signal?.addEventListener('abort', () => { clearTimeout(timeout); resolve(); }, { once: true });
  });
  if (ctx.signal?.aborted) return;
  try {
    // Jotai atom にモーション情報をセット（VirtualRobot が監視）
    // モーション完了を Promise で待つ
    const virtualDone: Promise<void> = ctx.setFlowMotion
      ? new Promise<void>((resolve, reject) => {
          let settled = false;
          const finish = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            ctx.signal?.removeEventListener('abort', abort);
            resolve();
          };
          const abort = () => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            ctx.signal?.removeEventListener('abort', abort);
            reject(new DOMException('Flow execution aborted', 'AbortError'));
          };
          const fail = (error: Error) => {
            if (settled) return;
            settled = true;
            window.clearTimeout(timeoutId);
            ctx.signal?.removeEventListener('abort', abort);
            reject(error);
          };
          const timeoutId = window.setTimeout(() => {
            if (!requireVirtualCompletion) { finish(); return; }
            if (settled) return;
            settled = true;
            ctx.signal?.removeEventListener('abort', abort);
            ctx.setFlowMotion?.(null);
            reject(new Error('歩行モーションの完了通知がタイムアウトしました。'));
          }, virtualTimeoutMs);
          if (ctx.signal?.aborted) abort();
          else ctx.signal?.addEventListener('abort', abort, { once: true });
          ctx.setFlowMotion!({
            motionNo,
            requestId: nextFlowRequestId(),
            timeline: getVirtualMotionTimeline(timeline, Boolean(ctx.robotService)),
            physicsEnabled,
            playBgm: !ctx.robotService,
            playNativeAudio: !ctx.robotService,
            onComplete: finish,
            onError: requireVirtualCompletion ? fail : undefined,
          });
        })
      : Promise.resolve();

    if (ctx.robotService && customGesture) {
      const [, summary] = await Promise.all([
        virtualDone,
        ctx.robotService.playCustomGesture(customGesture, { nodeId, signal: ctx.signal }),
      ]);
      logBatchResult(logTag, log, summary.results);
      log(`[${logTag}] ${motionLabel}（${summary.results.filter((result) => result.status === 'fulfilled').length}/${summary.results.length}台）`);
      ctx.onExecutionEvent?.({ type: 'motion', nodeId, nodeKind, motionId });
      return;
    }
    if (ctx.robotService && !skipRobotMotion) {
      const options = { nodeId, signal: ctx.signal };
      const [, summary] = await Promise.all([
        virtualDone,
        robotCommand ? robotCommand(ctx.robotService, options) : ctx.robotService.startMotion(motionNo, options),
      ]);
      logBatchResult(logTag, log, summary.results);
      log(`[${logTag}] ${motionLabel}（${summary.results.filter((result) => result.status === 'fulfilled').length}/${summary.results.length}台）`);
      ctx.onExecutionEvent?.({ type: 'motion', nodeId, nodeKind, motionId });
      return;
    }
    await virtualDone;
    log(`[${logTag}] ${motionLabel}`);
    ctx.onExecutionEvent?.({ type: 'motion', nodeId, nodeKind, motionId });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    if (requireVirtualCompletion) throw error;
    log(`[${logTag}] エラー: ${String(error)}`);
  }
};
